/**
 * Proofix Referral Tracking Utility
 * 
 * Handles referral code storage via:
 * - Cookie (30 days, for cross-session persistence)
 * - LocalStorage (backup for mobile browsers)
 * 
 * Rules:
 * - If new referral link clicked, overwrites previous
 * - Cookie/localStorage expires after 30 days
 * - Compatible with mobile browsers (iOS Safari, etc.)
 */

const COOKIE_NAME = 'proofix_ref';
const STORAGE_KEY = 'proofix_referral';
const COOKIE_DAYS = 30;

/**
 * Set a cookie with expiration
 */
function setCookie(name, value, days) {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  
  // Use SameSite=Lax for better cross-site compatibility
  // Secure flag only in production (HTTPS)
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires.toUTCString()}; path=/; SameSite=Lax${secure}`;
}

/**
 * Get a cookie value by name
 */
function getCookie(name) {
  const nameEQ = name + '=';
  const cookies = document.cookie.split(';');
  
  for (let i = 0; i < cookies.length; i++) {
    let cookie = cookies[i].trim();
    if (cookie.indexOf(nameEQ) === 0) {
      return decodeURIComponent(cookie.substring(nameEQ.length));
    }
  }
  return null;
}

/**
 * Delete a cookie
 */
function deleteCookie(name) {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
}

/**
 * Save referral data to both cookie and localStorage
 */
export function saveReferralCode(code, affiliateName = null) {
  if (!code) return;
  
  const referralData = {
    code: code.toLowerCase(),
    affiliate_name: affiliateName,
    saved_at: new Date().toISOString(),
    expires_at: new Date(Date.now() + COOKIE_DAYS * 24 * 60 * 60 * 1000).toISOString()
  };
  
  // Save to cookie
  setCookie(COOKIE_NAME, code.toLowerCase(), COOKIE_DAYS);
  
  // Save to localStorage (with full data for mobile backup)
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(referralData));
  } catch (e) {
    console.warn('Could not save referral to localStorage:', e);
  }
  
  console.log('[Referral] Saved referral code:', code);
}

/**
 * Get referral code from cookie or localStorage
 * Returns null if expired or not found
 */
export function getReferralCode() {
  // First try cookie
  const cookieCode = getCookie(COOKIE_NAME);
  if (cookieCode) {
    return cookieCode;
  }
  
  // Fallback to localStorage
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const data = JSON.parse(stored);
      
      // Check if expired
      if (data.expires_at && new Date(data.expires_at) > new Date()) {
        return data.code;
      } else {
        // Expired, clean up
        localStorage.removeItem(STORAGE_KEY);
      }
    }
  } catch (e) {
    console.warn('Could not read referral from localStorage:', e);
  }
  
  return null;
}

/**
 * Get full referral data (for display purposes)
 */
export function getReferralData() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const data = JSON.parse(stored);
      
      // Check if expired
      if (data.expires_at && new Date(data.expires_at) > new Date()) {
        return data;
      }
    }
  } catch (e) {
    console.warn('Could not read referral data:', e);
  }
  
  // Fallback to just the code
  const code = getCookie(COOKIE_NAME);
  if (code) {
    return { code, affiliate_name: null };
  }
  
  return null;
}

/**
 * Clear referral data (for testing or manual clear)
 */
export function clearReferralCode() {
  deleteCookie(COOKIE_NAME);
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    // Ignore
  }
}

/**
 * Check URL for referral parameter and save if present
 * Call this on app load or page navigation
 */
export async function checkAndSaveReferral(apiUrl) {
  const urlParams = new URLSearchParams(window.location.search);
  const refCode = urlParams.get('ref');
  
  if (!refCode) return null;
  
  console.log('[Referral] Detected referral code in URL:', refCode);
  
  // Validate the referral code with backend
  try {
    const response = await fetch(`${apiUrl}/api/affiliate/validate/${encodeURIComponent(refCode)}`);
    const data = await response.json();
    
    if (data.valid) {
      // Save the referral code
      saveReferralCode(refCode, data.affiliate_name);
      
      // Track the click
      await fetch(`${apiUrl}/api/affiliate/track-click?referral_code=${encodeURIComponent(refCode)}`, {
        method: 'POST'
      });
      
      // Remove ref from URL (clean URL) without reload
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('ref');
      window.history.replaceState({}, document.title, newUrl.pathname + newUrl.search);
      
      return { code: refCode, affiliate_name: data.affiliate_name };
    } else {
      console.warn('[Referral] Invalid referral code:', refCode);
    }
  } catch (error) {
    console.error('[Referral] Error validating referral code:', error);
    // Still save locally even if validation fails (offline support)
    saveReferralCode(refCode);
    return { code: refCode, affiliate_name: null };
  }
  
  return null;
}

/**
 * Hook-like function to use in React components
 */
export function useReferralCode() {
  return getReferralCode();
}

export default {
  saveReferralCode,
  getReferralCode,
  getReferralData,
  clearReferralCode,
  checkAndSaveReferral,
  useReferralCode
};
